import { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2, ShoppingBag, Check, Minus, Plus, MapPin, Info, Trash2, AlertTriangle, PlusCircle, Clock } from 'lucide-react';
import { resolvePickupInstructions } from '@/lib/shop/pickup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useCreatePickupOrder } from '@/hooks/usePickupOrder';
import { useActivePickupWindows, useHasPickupWindows, formatPickupWindowLabel } from '@/hooks/usePickupWindows';
import { track } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { ProductPickerDialog } from './ProductPickerDialog';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { toastFlowError } from '@/lib/toast/toastFlowError';
import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import { useTranslate } from '@/i18n';
/* ── Types ── */

export interface PreorderProduct {
  id: string;
  title: string;
  price: number; // kronor
  currencyCode: string;
  imageUrl?: string | null;
  pickupInstructions?: string | null;
  stockLimit?: number | null; // null = unlimited
  remaining?: number | null;  // null = unlimited
}

interface OrderDraftItem {
  product: PreorderProduct;
  quantity: number;
}

export interface PickupOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial product to add (single-product entry point) */
  product: PreorderProduct;
  producerId: string;
  producerName: string;
  pickupInstructions?: string | null;
  /** Surface that opened this dialog – used for analytics source_surface */
  sourceSurface?: string;
}

function formatPrice(amount: number, currency: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface InsufficientStockItem {
  product_id: string;
  title: string;
  requested_quantity: number;
  available_stock: number;
}

function parseStockError(errorMessage: string): InsufficientStockItem[] | null {
  try {
    // The RPC wraps the JSON in the error message
    const parsed = JSON.parse(errorMessage);
    if (parsed.error_key === 'insufficient_stock' && Array.isArray(parsed.items)) {
      return parsed.items;
    }
  } catch {
    // Try to detect the old format: "Det finns bara X st kvar av ..."
    if (errorMessage.includes('finns bara') || errorMessage.includes('inga fler kvar')) {
      return null; // old format, can't parse structured items
    }
  }
  return null;
}

export function PickupOrderDialog({
  open,
  onOpenChange,
  product,
  producerId,
  producerName,
  pickupInstructions,
  sourceSurface = 'unknown',
}: PickupOrderDialogProps) {
  const t = useTranslate();
  const { toast } = useToast();
  const mutation = useCreatePickupOrder();

  // Multi-item order draft
  const [items, setItems] = useState<OrderDraftItem[]>([]);
  const [email, setEmail] = useState('');
  const [emailPrefilled, setEmailPrefilled] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [stockWarning, setStockWarning] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState<string>('');
  const requestIdRef = useRef(crypto.randomUUID());

  // Fetch active pickup windows for this producer
  const { data: pickupWindows = [] } = useActivePickupWindows(producerId);
  const { data: hasAnyWindows = false } = useHasPickupWindows(producerId);

  // Windows exist but all expired/closed = block checkout
  const allWindowsExpired = hasAnyWindows && pickupWindows.length === 0;

  // Initialize with the trigger product when dialog opens
  const trackedOpenRef = useRef(false);
  useEffect(() => {
    if (open && items.length === 0) {
      const maxQty = getMaxQuantity(product);
      setItems([{ product, quantity: Math.min(1, maxQty) }]);
      setStockWarning(null);
    }
    if (open && !trackedOpenRef.current) {
      trackedOpenRef.current = true;
      track('shop_preorder_opened', {
        producer_id: producerId,
        product_id: product.id,
        source_surface: sourceSurface,
      });
    }
    if (!open) {
      trackedOpenRef.current = false;
    }
  }, [open]);

  // Prefill email from auth user
  useEffect(() => {
    if (!open || emailPrefilled) return;
    supabase.auth.getUser().then(({ data }) => {
      const userEmail = data?.user?.email;
      if (userEmail && !email) {
        setEmail(userEmail);
        setEmailPrefilled(true);
      }
    });
  }, [open, emailPrefilled, email]);

  const getMaxQuantity = (p: PreorderProduct): number => {
    if (p.remaining !== null && p.remaining !== undefined) return Math.max(0, p.remaining);
    if (p.stockLimit !== null && p.stockLimit !== undefined) return Math.max(0, p.stockLimit);
    return 99; // unlimited
  };

  const updateQuantity = useCallback((productId: string, delta: number) => {
    setItems(prev => prev.map(item => {
      if (item.product.id !== productId) return item;
      const max = getMaxQuantity(item.product);
      const newQty = Math.max(1, Math.min(max, item.quantity + delta));
      return { ...item, quantity: newQty };
    }));
    setStockWarning(null);
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  }, []);

  const addProduct = useCallback((p: PreorderProduct) => {
    setItems(prev => {
      const existing = prev.find(item => item.product.id === p.id);
      if (existing) {
        // Increase quantity if possible
        const max = getMaxQuantity(p);
        return prev.map(item =>
          item.product.id === p.id
            ? { ...item, quantity: Math.min(max, item.quantity + 1) }
            : item
        );
      }
      const max = getMaxQuantity(p);
      if (max <= 0) return prev; // out of stock
      return [...prev, { product: p, quantity: 1 }];
    });
    setPickerOpen(false);
  }, []);

  const totalPrice = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const currency = items[0]?.product.currencyCode || 'SEK';

  // Batch-fetch cover images from media_assets
  const productIds = items.map(i => i.product.id);
  const { data: coverMap } = useProductCoverImages(productIds);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStockWarning(null);

    if (!email || email.length < 3) {
      toastFlowError('E-post krävs', 'Ange en giltig e-postadress.');
      return;
    }

    if (items.length === 0) return;

    track('pickup_order_started', {
      producer_id: producerId,
      product_ids: items.map(i => i.product.id),
      qty_total: items.reduce((s, i) => s + i.quantity, 0),
    });

    try {
      const result = await mutation.mutateAsync({
        producer_id: producerId,
        items: items.map(i => ({ product_id: i.product.id, quantity: i.quantity })),
        customer_email: email.trim(),
        customer_name: name.trim() || undefined,
        customer_phone: phone.trim() || undefined,
        customer_note: note.trim() || undefined,
        client_request_id: requestIdRef.current,
        pickup_window_id: selectedWindowId || undefined,
      });

      track('pickup_order_completed', {
        order_id: result.order_id,
        producer_id: producerId,
        total_amount: result.total_amount,
      });

      setOrderNumber(result.order_number);
      setSubmitted(true);
    } catch (err: any) {
      const msg = err.message || '';

      // Try to parse structured stock error
      const stockItems = parseStockError(msg);
      if (stockItems) {
        // Auto-adjust quantities and show inline warning
        setItems(prev => prev.map(item => {
          const conflict = stockItems.find(s => s.product_id === item.product.id);
          if (!conflict) return item;
          return {
            ...item,
            quantity: Math.max(0, conflict.available_stock),
            product: { ...item.product, remaining: conflict.available_stock },
          };
        }).filter(item => item.quantity > 0));

        setStockWarning('Vi justerade antalet utifrån aktuellt lager.');
        toast({
          title: 'Antalet uppdaterades utifrån lager',
          description: 'Kontrollera beställningen och skicka igen.',
        });
        // Generate new request ID to avoid idempotency collision
        requestIdRef.current = crypto.randomUUID();
        return;
      }

      // Old-format stock error or generic Swedish stock message
      if (msg.includes('finns bara') || msg.includes('inga fler kvar')) {
        setStockWarning(msg);
        toast({
          title: 'Lagret räckte inte',
          description: 'Justera antalet och försök igen.',
        });
        requestIdRef.current = crypto.randomUUID();
        return;
      }

      // Non-destructive flow error – calm toast, log privately
      console.error('[PickupOrder] RPC failed:', msg);
      captureAppError(err, {
        feature: 'shop',
        action: 'create_pickup_order',
        producerId,
        requestId: requestIdRef.current,
      });
      logSystemEvent({
        level: 'error',
        event_key: 'pickup_order_rpc_failed',
        domain: 'shop',
        entity_type: 'order',
        producer_id: producerId,
        message: msg || 'Unknown RPC error',
        request_id: requestIdRef.current,
        context: buildSystemEventContext({ feature: 'shop', action: 'create_pickup_order', surface: 'pickup_order_dialog', rpc_name: 'create_pickup_order', product_ids: items.map(i => i.product.id) }),
      });
      toastFlowError('Kunde inte skapa beställning');
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSubmitted(false);
      setItems([]);
      setEmail('');
      setEmailPrefilled(false);
      setName('');
      setPhone('');
      setNote('');
      setOrderNumber('');
      setStockWarning(null);
      setSelectedWindowId('');
      requestIdRef.current = crypto.randomUUID();
    }, 300);
  };

  const formRef = useRef<HTMLFormElement | null>(null);

  // ── Derive pickup label for selected window ──
  const selectedPickupWindow = pickupWindows.find(w => w.id === selectedWindowId);
  const pickupLabel = selectedPickupWindow ? formatPickupWindowLabel(selectedPickupWindow) : null;

  // ── Success state ──
  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-testid="pickup-order-dialog">
          <div className="py-6 space-y-5" data-testid="pickup-order-success">
            {/* Header */}
            <div className="text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {t('order.thankYou')}
              </h3>
            </div>

            {/* Order details */}
            <div className="rounded-lg bg-muted/50 border border-border p-4 space-y-3 text-sm">
              {/* Order number */}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Ordernummer</span>
                <span className="font-mono font-medium text-foreground">{orderNumber}</span>
              </div>

              {/* Producer */}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Värd</span>
                <span className="font-medium text-foreground">{producerName}</span>
              </div>

              {/* Pickup time */}
              {pickupLabel && (
                <div className="flex justify-between items-start gap-4">
                  <span className="text-muted-foreground shrink-0">Upphämtning</span>
                  <span className="font-medium text-foreground text-right">{pickupLabel}</span>
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Product summary */}
              <div className="space-y-1.5">
                <span className="text-muted-foreground">Du beställde:</span>
                {items.map(item => (
                  <div key={item.product.id} className="flex justify-between items-center pl-2">
                    <span className="text-foreground truncate mr-2">{item.product.title}</span>
                    <span className="text-muted-foreground shrink-0">×{item.quantity}</span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Total */}
              <div className="flex justify-between items-center font-medium">
                <span className="text-foreground">Totalt</span>
                <span className="text-foreground">{formatPrice(totalPrice, currency)}</span>
              </div>
            </div>

            {/* Help text */}
            <p className="text-sm text-muted-foreground text-center max-w-xs mx-auto leading-relaxed">
              {producerName} hör av sig med information om upphämtning. En bekräftelse har skickats till din e-post.
            </p>

            <div className="text-center">
              <Button onClick={handleClose} className="mt-1">
                {t('order.cta.close')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Order form ──
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="pickup-order-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">{t('shop.label.preorderForPickup')}</DialogTitle>
          <DialogDescription>
            Reservera varor från {producerName}. Ingen betalning sker nu.
          </DialogDescription>
        </DialogHeader>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Stock warning banner */}
          {stockWarning && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{stockWarning}</p>
            </div>
          )}

          {/* Items list */}
          <div className="space-y-2">
            {items.map((item) => {
              const max = getMaxQuantity(item.product);
              const isLimited = item.product.remaining !== null && item.product.remaining !== undefined;
              return (
                <div key={item.product.id} className="rounded-lg bg-muted/50 border border-border p-3">
                  <div className="flex items-center gap-3">
                    {/* Image – media_assets SoT, fallback to legacy, then monogram */}
                    <ProductThumb
                      src={coverMap?.[item.product.id] ?? item.product.imageUrl}
                      alt={item.product.title}
                      title={item.product.title}
                      size="md"
                    />

                    {/* Title + price */}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground text-sm truncate">{item.product.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatPrice(item.product.price, item.product.currencyCode)} / st
                      </p>
                    </div>

                    {/* Remove */}
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => removeItem(item.product.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {/* Stepper row */}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.product.id, -1)}
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.product.id, 1)}
                        disabled={item.quantity >= max}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {formatPrice(item.product.price * item.quantity, item.product.currencyCode)}
                    </span>
                  </div>

                  {/* Stock hint */}
                  {isLimited && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {item.product.remaining === 0
                        ? t('status.soldOut')
                        : t('order.label.remainingToReserve', { count: item.product.remaining })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add more products */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            onClick={() => setPickerOpen(true)}
          >
            <PlusCircle className="h-4 w-4" />
            {t('order.cta.addMore')}
          </Button>

          {/* Total */}
          <div className="flex justify-between text-sm pt-1 border-t border-border">
            <span className="text-muted-foreground">Totalt ({items.reduce((s, i) => s + i.quantity, 0)} varor)</span>
            <span className="font-semibold text-foreground">{formatPrice(totalPrice, currency)}</span>
          </div>

          {/* Pickup window selection */}
          {pickupWindows.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Välj upphämtning *
              </Label>
              <RadioGroup value={selectedWindowId} onValueChange={setSelectedWindowId}>
                {pickupWindows.map(w => (
                  <div key={w.id} className="flex items-center gap-2">
                    <RadioGroupItem value={w.id} id={`pw-${w.id}`} data-testid={`pickup-order-window-option-${w.id}`} />
                    <Label htmlFor={`pw-${w.id}`} className="text-sm font-normal cursor-pointer" data-testid={`pickup-order-window-label-${w.id}`}>
                      {formatPickupWindowLabel(w)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {pickupWindows.length > 0 && !selectedWindowId && (
                <p className="text-xs text-amber-600">Välj en upphämtningstid för att fortsätta.</p>
              )}
            </div>
          )}

          {/* All windows expired – block ordering */}
          {allWindowsExpired && (
            <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                Inga bokningsbara upphämtningstider just nu
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                Värden har inte lagt till nya tider ännu. Kom gärna tillbaka senare.
              </p>
            </div>
          )}

          {/* Pickup info */}
          {(() => {
            const firstProduct = items[0]?.product;
            const resolved = resolvePickupInstructions({
              productInstructions: firstProduct?.pickupInstructions,
              producerInstructions: pickupInstructions,
            });
            return (
              <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  Hämtas på gården
                </div>
                <p className="text-xs text-muted-foreground pl-6">
                  {resolved.source === 'fallback'
                    ? (pickupWindows.length > 0 ? 'Välj en upphämtningstid ovan.' : 'Vi hör av oss för att bestämma tid för upphämtning.')
                    : resolved.text}
                </p>
                {resolved.source === 'product' && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted rounded-full px-2 py-0.5 ml-6">
                    <Info className="h-3 w-3" />
                    Särskilt för denna produkt
                  </span>
                )}
              </div>
            );
          })()}

          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="pickup-email">E-post *</Label>
            <Input
              id="pickup-email"
              type="email"
              required
              placeholder="din@epost.se"
              value={email}
              onChange={e => setEmail(e.target.value)}
              data-testid="pickup-order-email"
            />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="pickup-name">Namn <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
            <Input
              id="pickup-name"
              placeholder="Ditt namn"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="pickup-order-name"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="pickup-phone">Telefon <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
            <Input
              id="pickup-phone"
              type="tel"
              placeholder="070-123 45 67"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              data-testid="pickup-order-phone"
            />
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <Label htmlFor="pickup-note">Meddelande <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
            <Textarea
              id="pickup-note"
              placeholder="Berätta gärna om du har särskilda önskemål..."
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              data-testid="pickup-order-note"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Ingen betalning sker nu. Värden kontaktar dig med information om upphämtning.
          </p>

          <Button
            type="submit"
            className="w-full gap-2"
            disabled={mutation.isPending || !email || items.length === 0 || allWindowsExpired || (pickupWindows.length > 0 && !selectedWindowId)}
            data-testid="pickup-order-submit"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShoppingBag className="h-4 w-4" />
            )}
            {mutation.isPending ? 'Skickar beställning...' : 'Skicka beställning'}
          </Button>
        </form>

        {/* Sticky CTA for small viewports to avoid hidden submit below fold */}
        <div className="md:hidden sticky bottom-0 left-0 right-0 p-3 bg-card/90 backdrop-blur-sm border-t border-border">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Totalt ({items.reduce((s, i) => s + i.quantity, 0)} varor)</div>
              <div className="font-semibold">{formatPrice(totalPrice, currency)}</div>
            </div>
            <div className="w-1/2">
              <Button
                onClick={() => formRef.current?.requestSubmit()}
                className="w-full"
                disabled={mutation.isPending || !email || items.length === 0 || allWindowsExpired || (pickupWindows.length > 0 && !selectedWindowId)}
              >
                {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
                {mutation.isPending ? 'Skickar beställning...' : 'Skicka beställning'}
              </Button>
            </div>
          </div>
        </div>

        {/* Product picker for adding more items */}
        <ProductPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          producerId={producerId}
          producerName={producerName}
          existingProductIds={items.map(i => i.product.id)}
          onSelect={addProduct}
        />
      </DialogContent>
    </Dialog>
  );
}
